import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { tick as tickSim } from '@sim/core/tick';
import {
  buyObservableSlot,
  canPlace,
  clearAll,
  erase,
  place,
  reconfigureMachine,
  redirectConveyor,
  removeBase,
  removeObservableSlot,
  setObservableCompletesTo,
  setObservablePeriod,
} from '@sim/core/editing';
import type {
  BuildRequest,
  BuyObservableSlotResult,
  ConfigUpdate,
  EraseResult,
  PlaceResult,
  ReconfigureResult,
  SetObservableCompletesToResult,
  RemoveObservableSlotResult,
  SetObservablePeriodResult,
} from '@sim/core/editing';
import { findEntityAt } from '@sim/core/grid';
import type { SimEvent } from '@sim/core/events';
import type { SimState } from '@sim/core/state';
import type { Direction, EntityId, GridPos } from '@sim/core/types';
import {
  addSubscriberFunction,
  canEditSubscription,
  growSubscription as growSubscriptionCore,
  isEdgeGrowable,
  isSubscriptionEmpty,
  isSubscriberRegion,
  isSubscriberSlotFrozen,
  replaceSubscriberFunction,
  subscriberDestroyCorner,
  subscriberSlotInfo,
} from '@sim/core/subscriber';
import type {
  SubscriberColumn,
  SubscriberDestroyAnchor,
  SubscriberFunctionKind,
  SubscriberSlotInfo,
} from '@sim/core/subscriber';
import { toggleSubscribe } from '@sim/core/subscription';
import { activeEntityIds } from '@sim/core/routing';
import { applyExpansions } from '@sim/core/expansion';
import { STAGE1_EXPANSIONS, createStage1State } from '@sim/content/stage1-layout';
import { loadGame, saveGame } from './game-save';

export interface SubscriptionChange {
  subscribed: boolean;
}

const HISTORY_LIMIT = 10;

@Injectable({ providedIn: 'root' })
export class SimEngineService {
  private readonly stateSignal = signal<SimState>(loadGame() ?? createStage1State());
  /** tick()/place()/erase() mutate entities in place, so a `SimState` read before one of those calls is stale afterwards — always re-read `state()` rather than holding a reference. */
  readonly state = this.stateSignal.asReadonly();
  readonly events = new Subject<SimEvent[]>();
  readonly layoutChanged = new Subject<void>();
  readonly subscriptionChanged = new Subject<SubscriptionChange>();
  readonly historyRestored = new Subject<void>();

  private readonly undoStack = signal<SimState[]>([]);
  private readonly redoStack = signal<SimState[]>([]);
  readonly canUndo = computed(() => this.undoStack().length > 0);
  readonly canRedo = computed(() => this.redoStack().length > 0);

  private readonly sandboxFactory = signal<(() => SimState) | null>(null);
  /** True while the live state is a throwaway one and the autosave still holds the real game. */
  readonly sandboxed = computed(() => this.sandboxFactory() !== null);
  private parked: { state: SimState; undoStack: SimState[]; redoStack: SimState[] } | null = null;

  /**
   * `take` and `takeUntil` both end the subscription from inside a handler, so a tick can flip
   * `subscribed` with nothing else knowing. Announcing it here is what keeps the sub/unsub control
   * honest — left unannounced it still reads "subscribed", and the next click on it silently
   * re-subscribes instead of unsubscribing.
   */
  tick(): void {
    const wasSubscribed = this.stateSignal().subscribed;
    const { state, events } = tickSim(this.stateSignal());
    this.publish(state);
    if (events.length > 0) {
      this.events.next(events);
    }
    this.notifySubscriptionChange(wasSubscribed, state);
  }

  place(request: BuildRequest, pos: GridPos): PlaceResult {
    return this.mutate(
      (state) => place(state, request, pos),
      (result): result is Extract<PlaceResult, { ok: true }> => result.ok,
      (result, state, before) => {
        if (result.events && result.events.length > 0) this.events.next(result.events);
        this.layoutChanged.next();
        this.notifySubscriptionChange(before.subscribed, state);
      },
    );
  }

  reconfigureMachine(pos: GridPos, update: ConfigUpdate): ReconfigureResult {
    return this.mutate(
      (state) => reconfigureMachine(state, pos, update),
      (result): result is Extract<ReconfigureResult, { ok: true }> => result.ok,
      () => this.layoutChanged.next(),
    );
  }

  buyObservableSlot(pos: GridPos): BuyObservableSlotResult {
    return this.mutate(
      (state) => buyObservableSlot(state, pos),
      (result): result is Extract<BuyObservableSlotResult, { ok: true }> => result.ok,
      () => this.layoutChanged.next(),
    );
  }

  removeObservableSlot(pos: GridPos): RemoveObservableSlotResult {
    return this.mutate(
      (state) => removeObservableSlot(state, pos),
      (result): result is Extract<RemoveObservableSlotResult, { ok: true }> => result.ok,
      () => this.layoutChanged.next(),
    );
  }

  setObservablePeriod(pos: GridPos, periodTicks: number): SetObservablePeriodResult {
    return this.mutate(
      (state) => setObservablePeriod(state, pos, periodTicks),
      (result): result is Extract<SetObservablePeriodResult, { ok: true }> => result.ok,
      () => this.layoutChanged.next(),
    );
  }

  setObservableCompletesTo(
    pos: GridPos,
    subjectId: EntityId | null,
  ): SetObservableCompletesToResult {
    return this.mutate(
      (state) => setObservableCompletesTo(state, pos, subjectId),
      (result): result is Extract<SetObservableCompletesToResult, { ok: true }> => result.ok,
      () => this.layoutChanged.next(),
    );
  }

  redirectConveyor(pos: GridPos, direction: Direction): boolean {
    return this.mutate(
      (state) => redirectConveyor(state, pos, direction),
      (changed): changed is true => changed,
      () => this.layoutChanged.next(),
    );
  }

  addSubscriberFunction(column: SubscriberColumn, kind: SubscriberFunctionKind): boolean {
    return this.mutate(
      (state) => addSubscriberFunction(state, column, kind),
      (added): added is true => added,
      () => this.layoutChanged.next(),
    );
  }

  replaceSubscriberFunction(
    column: SubscriberColumn,
    row: number,
    kind: SubscriberFunctionKind,
  ): boolean {
    return this.mutate(
      (state) => replaceSubscriberFunction(state, column, row, kind),
      (replaced): replaced is true => replaced,
      () => this.layoutChanged.next(),
    );
  }

  canEditSubscription(): boolean {
    return canEditSubscription(this.stateSignal());
  }

  /** Grows or collapses one wall of the Subscription by one cell — see `growSubscription` in `@sim/core/subscriber`. */
  growSubscription(edge: Direction, direction: 1 | -1): boolean {
    return this.mutate(
      (state) => growSubscriptionCore(state, edge, direction),
      (grown): grown is true => grown,
      () => this.layoutChanged.next(),
    );
  }

  /** Whether `edge` may currently grow — never the entry wall, never anything but the committed direction once one exists. */
  isEdgeGrowable(edge: Direction): boolean {
    return isEdgeGrowable(this.stateSignal(), edge);
  }

  erase(pos: GridPos): EraseResult {
    return this.mutate(
      (state) => erase(state, pos),
      (result): result is Extract<EraseResult, { ok: true }> => result.ok,
      (result) => {
        if (result.events.length > 0) this.events.next(result.events);
        this.layoutChanged.next();
      },
    );
  }

  removeBase(): EraseResult {
    return this.mutate(
      (state) => removeBase(state),
      (result): result is Extract<EraseResult, { ok: true }> => result.ok,
      (result) => {
        if (result.events.length > 0) this.events.next(result.events);
        this.layoutChanged.next();
      },
    );
  }

  clearAll(): number {
    return this.mutate(
      (state) => clearAll(state),
      (result): result is { refund: number; events: SimEvent[] } =>
        result.refund > 0 || result.events.length > 0,
      ({ events }) => {
        if (events.length > 0) this.events.next(events);
        this.layoutChanged.next();
      },
    ).refund;
  }

  resetGame(): void {
    const fresh = (this.sandboxFactory() ?? createStage1State)();
    this.stateSignal.set(fresh);
    this.undoStack.set([]);
    this.redoStack.set([]);
    this.persist(fresh);
    this.layoutChanged.next();
    this.historyRestored.next();
  }

  /**
   * Parks the real game — state and history both — behind a throwaway one built by `factory`, whose
   * edits never reach the autosave. What lets debug mode hand out a fully-unlocked map without the
   * player's own save being the thing it writes over.
   */
  enterSandbox(factory: () => SimState): void {
    if (this.sandboxFactory()) return;
    this.parked = {
      state: this.stateSignal(),
      undoStack: this.undoStack(),
      redoStack: this.redoStack(),
    };
    this.sandboxFactory.set(factory);
    this.swapIn(factory(), [], []);
  }

  /** Puts the parked game back exactly as {@link enterSandbox} found it. */
  exitSandbox(): void {
    const parked = this.parked;
    if (!parked) return;
    this.parked = null;
    this.sandboxFactory.set(null);
    this.swapIn(parked.state, parked.undoStack, parked.redoStack);
  }

  private swapIn(state: SimState, undoStack: SimState[], redoStack: SimState[]): void {
    this.stateSignal.set(state);
    this.undoStack.set(undoStack);
    this.redoStack.set(redoStack);
    this.layoutChanged.next();
    this.historyRestored.next();
  }

  toggleSubscribe(): void {
    this.mutate(
      (state) => toggleSubscribe(state),
      (events): events is SimEvent[] => Array.isArray(events),
      (events, state) => {
        if (events.length > 0) this.events.next(events);
        this.subscriptionChanged.next({ subscribed: state.subscribed });
      },
    );
  }

  /** Placing an operator on a live pipe implicitly unsubscribes as a side effect — only announce it when that actually happened. */
  private notifySubscriptionChange(wasSubscribed: boolean, state: SimState): void {
    if (state.subscribed !== wasSubscribed) {
      this.subscriptionChanged.next({ subscribed: state.subscribed });
    }
  }

  /** Shared undo/publish plumbing for every state-mutating edit: clone, run `op`, and — only if `isOk` accepts the result — push undo history, publish the new state, and let the caller react via `onSuccess`. */
  private mutate<R, S extends R>(
    op: (state: SimState) => R,
    isOk: (result: R) => result is S,
    onSuccess: (result: S, state: SimState, before: SimState) => void,
  ): R {
    const state = this.stateSignal();
    const before = structuredClone(state);
    const result = op(state);
    if (isOk(result)) {
      this.pushUndo(before);
      this.publish(state);
      onSuccess(result, state, before);
    }
    return result;
  }

  undo(): void {
    const stack = this.undoStack();
    if (stack.length === 0) return;
    const previous = stack[stack.length - 1];
    this.undoStack.set(stack.slice(0, -1));
    this.redoStack.update((redo) => [...redo, structuredClone(this.stateSignal())]);
    this.stateSignal.set(previous);
    this.persist(previous);
    this.layoutChanged.next();
    this.historyRestored.next();
  }

  redo(): void {
    const stack = this.redoStack();
    if (stack.length === 0) return;
    const next = stack[stack.length - 1];
    this.redoStack.set(stack.slice(0, -1));
    this.undoStack.update((undo) =>
      this.capHistory([...undo, structuredClone(this.stateSignal())]),
    );
    this.stateSignal.set(next);
    this.persist(next);
    this.layoutChanged.next();
    this.historyRestored.next();
  }

  private pushUndo(before: SimState): void {
    this.undoStack.update((stack) => this.capHistory([...stack, before]));
    this.redoStack.set([]);
  }

  private capHistory(stack: SimState[]): SimState[] {
    return stack.length > HISTORY_LIMIT ? stack.slice(stack.length - HISTORY_LIMIT) : stack;
  }

  canPlace(request: BuildRequest, pos: GridPos): boolean {
    return canPlace(this.stateSignal(), request, pos).ok;
  }

  canErase(pos: GridPos): boolean {
    const state = this.stateSignal();
    const target = findEntityAt(state, pos);
    if (target === undefined || target.kind === 'sink') return false;
    return !activeEntityIds(state).has(target.id);
  }

  isSubscriberRegion(pos: GridPos): boolean {
    return isSubscriberRegion(this.stateSignal(), pos);
  }

  isSubscriberSlotFrozen(pos: GridPos): boolean {
    return isSubscriberSlotFrozen(this.stateSignal(), pos);
  }

  subscriberSlotInfo(pos: GridPos): SubscriberSlotInfo | null {
    const state = this.stateSignal();
    return state.subscriber && state.base
      ? subscriberSlotInfo(state.subscriber, state.base, pos)
      : null;
  }

  /** Where to anchor the Subscriber's destroy control — null until a base is placed. */
  subscriberDestroyCorner(): SubscriberDestroyAnchor | null {
    return subscriberDestroyCorner(this.stateSignal());
  }

  /** Whether the placed Subscriber has any function in any column — gates the destroy confirmation. */
  isSubscriptionEmpty(): boolean {
    const box = this.stateSignal().subscriber;
    return box === null || isSubscriptionEmpty(box);
  }

  /**
   * Every state change funnels through here, so it is the one place an expansion gate can be
   * re-checked without each caller having to remember to. `peakCash` is refreshed first because
   * cash-branch gates read it, not the live balance.
   */
  private publish(state: SimState): void {
    state.economy.peakCash = Math.max(state.economy.peakCash, state.economy.cash);
    const expanded = applyExpansions(state, STAGE1_EXPANSIONS);
    this.stateSignal.set({ ...state });
    this.persist(state);
    if (expanded.length > 0) this.layoutChanged.next();
  }

  /** The autosave belongs to the real game alone — a sandbox lives and dies in memory. */
  private persist(state: SimState): void {
    if (!this.sandboxFactory()) saveGame(state);
  }
}
